import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LogViewer } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { createPortal } from 'react-dom';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  followButton: {
    height: '30px', // Match IconButton size (small)
    fontSize: '0.8125rem',
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'none',
    minWidth: 'auto',
    borderRadius: theme.shape.borderRadius,
    marginRight: theme.spacing(1), // Gap before search box
  },
  headerTitle: {
    order: -2,
    marginRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexGrow: 1,
    textAlign: 'left',
  },
}));

type LogsMap = Record<string, string[]>;
const DEFAULT_LINE_NUMBER_OFFSET = 68; // 60px width + 8px margin-right from LogViewer styles

export const AutoFollowTaskLogStream: React.FC<{ 
  logs: LogsMap; 
  activeStepId?: string; 
  follow?: boolean;
  followActiveStep?: boolean;
  onToggleFollow?: () => void;
  headerTitle?: React.ReactNode;
}> = ({ logs, activeStepId, follow = true, followActiveStep, onToggleFollow, headerTitle }) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null);
  const wasAtBottomRef = useRef<boolean>(true);
  const [lineNumberOffset, setLineNumberOffset] = useState<number>(DEFAULT_LINE_NUMBER_OFFSET);

  const text = useMemo(
    () => {
      let lines: string[];
      if (activeStepId && logs[activeStepId]) {
        lines = logs[activeStepId] || [];
      } else {
        // Fallback: merge all logs (legacy behavior)
        lines = Object.values(logs).flat();
      }
      // Deduplicate log lines to handle SSE reconnection replays
      // (e.g., when switching browser tabs causes the EventSource to reconnect)
      const seen = new Set<string>();
      const unique: string[] = [];
      for (const line of lines) {
        if (!seen.has(line)) {
          seen.add(line);
          unique.push(line);
        }
      }
      // Decode ANSI codes that were encoded by the backend
      return unique.join('\n').replace(/§\[/g, '\x1b[');
    },
    [logs, activeStepId],
  );

  // Find and attach to the LogViewer's header container
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    
    // Look for the header container with controls
    const findHeader = () => {
      // Try multiple selectors to find the header
      let header = root.querySelector<HTMLElement>('[class*="BackstageLogViewer-header"]');
      if (!header) {
        // Fallback: look for the Box component that contains the controls
        header = root.querySelector<HTMLElement>('[class*="jss"][class*="header"]');
      }
      if (!header) {
        // Another fallback: look for parent of search TextField
        const searchBox = root.querySelector('input[placeholder="Search"]');
        if (searchBox) {
          header = searchBox.closest('[class*="MuiBox-root"]') as HTMLElement;
        }
      }
      if (header) {
        // console.log('Found header:', header);
        setHeaderContainer(header);
        return true;
      }
      return false;
    };
    
    // Try with a small delay to ensure LogViewer has rendered
    const timeoutId = setTimeout(() => {
      if (!findHeader()) {
        // If still not found, observe DOM
        const observer = new MutationObserver(() => {
          if (findHeader()) {
            observer.disconnect();
          }
        });
        observer.observe(root, { childList: true, subtree: true });
        
        // Cleanup after 5 seconds if still not found
        setTimeout(() => observer.disconnect(), 5000);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Measure the width of the line number gutter to align the header title with log text
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const measureOffset = () => {
      const lineNumberEl = root.querySelector<HTMLElement>('[class*="BackstageLogViewer-lineNumber"]');
      if (!lineNumberEl) {
        return false;
      }
      const rect = lineNumberEl.getBoundingClientRect();
      if (rect.width === 0) {
        return false;
      }
      const style = window.getComputedStyle(lineNumberEl);
      const marginRight = parseFloat(style.marginRight || '0');
      setLineNumberOffset(rect.width + marginRight);
      return true;
    };

    let cancelled = false;

    const measured = measureOffset();
    if (measured) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (cancelled) return;
      if (measureOffset()) {
        observer.disconnect();
        cancelled = true;
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [logs]);

  // Ref to track if we're currently performing a programmatic scroll
  const isProgrammaticScrollRef = useRef<boolean>(false);
  // Ref to cache the scroller element so we don't re-query the DOM every time
  const scrollerRef = useRef<HTMLElement | null>(null);
  // True for ~250 ms after any user-initiated scroll input (wheel/touch/key).
  // While true, the auto-scroll effect refuses to fire so it can't fight the
  // user's wheel — this is what eliminates the shake near the bottom.
  const userScrollActiveRef = useRef<boolean>(false);
  const userScrollTimeoutRef = useRef<number | null>(null);

  const findScroller = useCallback((root: HTMLElement): HTMLElement | null => {
    // Return cached scroller if still valid
    if (scrollerRef.current && scrollerRef.current.clientHeight > 0 && root.contains(scrollerRef.current)) {
      return scrollerRef.current;
    }
    let scroller = root.querySelector<HTMLElement>('[class*="BackstageLogViewer-log"]');
    if (!scroller || scroller.clientHeight === 0) {
      scroller = root.querySelector<HTMLElement>('div[class*="jss"] > div');
    }
    if (!scroller || scroller.clientHeight === 0) {
      const allDivs = root.querySelectorAll<HTMLElement>('div');
      for (const el of Array.from(allDivs)) {
        const style = window.getComputedStyle(el);
        const hasScroll = (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
                         el.clientHeight > 0 &&
                         el.scrollHeight > el.clientHeight;
        if (hasScroll) {
          scroller = el;
          break;
        }
      }
    }
    const result = scroller && scroller.clientHeight > 0 ? scroller : null;
    scrollerRef.current = result;
    return result;
  }, []);

  // Track user scroll position to determine if they've manually scrolled up,
  // and detect "user is actively scrolling right now" so we can pause
  // auto-scroll for the duration of their input.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const markUserScrolling = () => {
      userScrollActiveRef.current = true;
      if (userScrollTimeoutRef.current !== null) {
        window.clearTimeout(userScrollTimeoutRef.current);
      }
      // Inertial wheel/touchpad scrolls take ~150–200 ms to settle;
      // 250 ms gives a comfortable dead-zone before auto-scroll resumes.
      userScrollTimeoutRef.current = window.setTimeout(() => {
        userScrollActiveRef.current = false;
        userScrollTimeoutRef.current = null;
      }, 250);
    };

    const handleScroll = () => {
      // Ignore scroll events caused by our own programmatic scrolling
      if (isProgrammaticScrollRef.current) return;

      const scroller = findScroller(root);
      if (!scroller) return;

      // 8 px threshold (was 30) — combined with userScrollActiveRef this
      // stops "still at bottom" being true while the user is mid-wheel.
      const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      wasAtBottomRef.current = distanceFromBottom < 8;
    };

    let scrollerElement: HTMLElement | null = null;
    let attached = false;

    const attachListener = () => {
      if (attached) return true;
      const scroller = findScroller(root);
      if (scroller) {
        wasAtBottomRef.current = true;
        scroller.addEventListener('scroll', handleScroll, { passive: true });
        // User-input listeners on the scroller cover the input gestures that
        // produce scrolling but don't necessarily fire 'scroll' immediately.
        scroller.addEventListener('wheel', markUserScrolling, { passive: true });
        scroller.addEventListener('touchmove', markUserScrolling, { passive: true });
        scroller.addEventListener('keydown', markUserScrolling, { passive: true });
        scrollerElement = scroller;
        attached = true;
        return true;
      }
      return false;
    };

    const detachListeners = () => {
      if (scrollerElement && attached) {
        scrollerElement.removeEventListener('scroll', handleScroll);
        scrollerElement.removeEventListener('wheel', markUserScrolling);
        scrollerElement.removeEventListener('touchmove', markUserScrolling);
        scrollerElement.removeEventListener('keydown', markUserScrolling);
      }
      if (userScrollTimeoutRef.current !== null) {
        window.clearTimeout(userScrollTimeoutRef.current);
        userScrollTimeoutRef.current = null;
      }
    };

    if (!attachListener()) {
      const observer = new MutationObserver(() => {
        if (attachListener()) {
          observer.disconnect();
        }
      });
      observer.observe(root, { childList: true, subtree: true });

      const timeouts = [100, 300, 500, 1000].map(delay =>
        setTimeout(() => {
          if (attachListener()) {
            observer.disconnect();
          }
        }, delay)
      );

      return () => {
        observer.disconnect();
        timeouts.forEach(t => clearTimeout(t));
        detachListeners();
      };
    }

    return detachListeners;
  }, [findScroller]);

  // Auto-scroll to bottom when new logs arrive, only if the user was already
  // at the bottom AND is not currently scrolling. Runs in a rAF (not a
  // setTimeout) so the scroll happens in the same frame as the new line —
  // there is no visible intermediate frame to produce a jump/shake.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !wasAtBottomRef.current || userScrollActiveRef.current) return;

    const rafId = requestAnimationFrame(() => {
      // Re-check inside the rAF: state may have changed in the meantime.
      if (!wasAtBottomRef.current || userScrollActiveRef.current) return;

      const scroller = findScroller(root);
      if (!scroller) return;

      isProgrammaticScrollRef.current = true;
      scroller.scrollTop = scroller.scrollHeight;
      // Release the guard after the browser has processed the scroll event.
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [text, follow, findScroller]);

  // Create a wrapper div to insert at the beginning with proper order
  const headerPortals = headerContainer ? [
    headerTitle
      ? createPortal(
          <div
            className={styles.headerTitle}
            key="log-title"
            style={{ paddingLeft: lineNumberOffset ?? DEFAULT_LINE_NUMBER_OFFSET }}
          >
            {headerTitle}
          </div>,
          headerContainer,
        )
      : null,
    followActiveStep !== undefined && onToggleFollow
      ? createPortal(
          <div style={{ order: -1, display: 'flex', alignItems: 'center' }} key="follow-button">
            <Button
              size="small"
              variant={followActiveStep ? 'contained' : 'outlined'}
              color="primary"
              onClick={onToggleFollow}
              className={styles.followButton}
            >
              {followActiveStep ? 'Following' : 'Follow'}
            </Button>
          </div>,
          headerContainer,
        )
      : null,
  ].filter(Boolean) : null;

  return (
    <div ref={containerRef} className={styles.root}>
      <LogViewer text={text} />
      {headerPortals}
    </div>
  );
};

export default AutoFollowTaskLogStream;


